<template>
  <b-container class="registration">
    <b-row class="registration-content text-center" h-align="center">
      <b-col cols="12">
        <div>
          <p v-if="webinarInfo.description">{{ webinarInfo.description }}</p>
        </div>

        <div v-if="displayStartTime" class="start-time">
          <p>{{ displayStartTime }}</p>
          <b-button variant="link" @click="showInMyTimeZone">Show In My Time Zone</b-button>
        </div>
      </b-col>

      <b-col cols="12">
        <router-view
          :fields="fields" :questions="questions"
          @submit="register">
        </router-view>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <registration-footer></registration-footer>
      </b-col>

      <captcha
        v-if="verifyHumanRegistrant"
        @onCaptchaToken="onCaptchaToken"
        ref="captcha"
      ></captcha>
    </b-row>
  </b-container>
</template>

<script>
import g2wRegistration from '@/store/modules/g2w-registration';
import recordingModule from '@/store/modules/recording';
import Captcha from '@/components/captcha';

import RegistrationForm from './registration-form';
import RegistrationFooter from './registration-footer';

const RegistrationWebinar = {
  name: 'RegistrationWebinar',

  data() {
    return {
        formFields: null,
        verifyHumanRegistrant: false
    }
  },

  props: ['webinarKey', 'recurrenceKey'],

  components: {
    RegistrationForm,
    RegistrationFooter,
    Captcha
  },

  computed: {
    webinarInfo() {
      return this.$store.state.g2wWebinar.webinarInfo;
    },

    branding() {
      return this.$store.state.g2wWebinar.branding;
    },

    displayStartTime() {
      const webinarTimes = this.webinarInfo.times || this.webinarInfo.webinarTimes;
      return new Date(webinarTimes[0].startTime).toString();
    },

    fields() {
      return this.$store.state.g2wRegistration.fields;
    },

    questions() {
      return this.$store.state.g2wRegistration.questions;
    },

    registrant() {
      return this.$store.state.g2wRegistration.registrant;
    },

    registered() {
      return (
        this.webinarInfo
        && this.registrant
        && this.registrant.status === 'APPROVED'
      );
    }
  },

  watch: {
      webinarInfo(newValue) {
          if (newValue) {
              this.verifyHumanRegistrant = newValue.verifyHumanRegistrant;
          }
      }
  },

  created() {
    this.$store.registerModule('g2wRegistration', g2wRegistration);

    this.$store
      .dispatch('g2wRegistration/getRegistrationFields', {
        webinarKey: this.webinarKey
      })
      .catch((error) => this.logger.log('cannot get registration fields', { error }));
  },

  methods: {
    showInMyTimeZone() {
      this.logger.log('showInMyTimeZone not implemented');
    },

    async register(data) {
      this.logger.log('register', {data, verifyHumanRegistrant: this.verifyHumanRegistrant});

      if (this.verifyHumanRegistrant && !data.humanRegistrantToken) {
          this.formFields = data;
          this.$refs.captcha.execute();
          return;
      }

      const payload = {webinarKey: this.webinarKey, data};

      try {
        const registration = await this.$store.dispatch('g2wRegistration/registerForWebinar', payload);
        await this.onRegistrationSuccess(registration);
      } catch (error) {
        // todo: 403 = webinar over or unknown?
          let info = 'unknown error';
          if (error.response) {
              info = await error.response.json();
              if (error.response.status === 409) {
                  await this.onRegistrationSuccess(info);
                  return;
              }
          }
          this.logger.error('cannot register', {info, error});
      }
    },

    async onRegistrationSuccess(registration) {
      this.logger.log('registered', {registration});
      const webinarKey = this.webinarKey;
      const allSessionsOver = this.$store.getters['g2wWebinar/allSessionsOver'];

      if (allSessionsOver) {
        if (!this.$store.state.recording) {
          this.$store.registerModule('recording', recordingModule);
        }
        let recordingKey;

        if (registration.asset) {
          const recordingAssets = await this.$store.dispatch('recording/getRecordingAssets', {
            webinarKey,
            registrantKey: registration.registrantKey,
            assetType: 'REGISTRATION'
          });
          recordingKey = recordingAssets.recordingKey;
        } else {
          const recordings = await this.$store.dispatch('g2wWebinar/getRecordings', {webinarKey});
          this.logger.log(`found ${recordings.length} recordings`);

          if (recordings.length) {
            recordingKey = recordings[0].recordingKey;

            await this.$store.dispatch('recording/getWebinarRecording', {
              webinarKey,
              recordingKey,
              registrantKey: registration.registrantKey
            });
          }
        }

        this.$router.push({
          name: 'view-webinar-recording',
          params: {
            webinarKey,
            registrantKey: registration.registrantKey,
            recordingKey,
            controls: true
          }
        });
      } else {
        // future session
        this.navigateToConfirmation(registration.registrantKey);
      }
    },

    navigateToConfirmation(registrantKey) {
      this.$router.push({
        name: 'registration-confirmation',
        params: {
          webinarKey: this.webinarKey,
          registrantKey
        }
      });
    },

    onCaptchaToken(token) {
        this.formFields.humanRegistrantToken = token;
        this.formFields.humanVerificationEngine = 'recaptcha';
        this.register(this.formFields);
    }
  }
};

export default RegistrationWebinar;
</script>

<style lang="sass">

  .registration
    height: 100%
    overflow-y: auto

    .registration-content
      padding: 15px 0

    .start-time
      margin-bottom: 30px

      p
        margin-bottom: 0

    .text-center
      text-align: center

    .registration-footer
      overflow: auto
      max-height: 160px

</style>
